import { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Checkbox, Grid, IconButton, TextField, Typography } from "@mui/material";
import { CheckSquareFill, ChevronDown, DashSquare, Square, ThreeDots, ThreeDotsVertical } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { BudgetBucketNodeItem, BudgetItem, BucketNodeItem, BucketNodeSnapshotResponse, CurrentUser, DateTime, FamilyBucketLabel, FamilyBucketPluralLabel, SortBucketNodesHierarchically } from "../../types";
import DropdownMenu from "../Inputs/DropdownMenu";
import Divider from "../Inputs/Divider";
import ModalFooterSaveCancel from "./ModalFooterSaveCancel";
import ModalHeader from "./ModalHeader";
import EditTimePeriodDate from "./EditTimeperiodDate";

interface BudgetBucketNodeExtended {
	item: BudgetBucketNodeItem,
	selected: boolean,
	active: boolean,
	isNew?: boolean,
	isUpdated?: boolean,
}

const EditBudgetBucketNodes = (props: any) => {
	const budget = props.Item as BudgetItem;
	const bucketTypeKey = props.BucketTypeKey as string;
	const snapshot: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
	const isMobile: boolean = useSelector((state: any) => state.isMobile.value);
	const user: CurrentUser = useSelector((state: any) => state.userData.value);
	const bucketLabel = FamilyBucketLabel(user, bucketTypeKey, bucketTypeKey);
	const bucketPluralLabel = FamilyBucketPluralLabel(user, bucketTypeKey, bucketTypeKey);

	const availableNodes = useMemo(() => {
		return SortBucketNodesHierarchically(
			(snapshot?.nodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase()),
			snapshot?.relations);
	}, [snapshot, bucketTypeKey]);
	const nodeById = useMemo(() => {
		return availableNodes.reduce((result, node) => {
			result[node.bucketNodeId] = node;
			return result;
		}, {} as Record<number, BucketNodeItem>);
	}, [availableNodes]);

	const [links, setLinks] = useState<BudgetBucketNodeExtended[]>(() => BuildExtendedLinks(budget.bucketNodes ?? [], bucketTypeKey, nodeById));
	const [showStart, setShowStart] = useState(false);
	const [showEnd, setShowEnd] = useState(false);

	useEffect(() => {
		setLinks(BuildExtendedLinks(budget.bucketNodes ?? [], bucketTypeKey, nodeById));
	}, [budget.bucketNodes, bucketTypeKey, nodeById]);

	const activeLinks = links.filter(link => link.active);
	const inactiveLinks = links.filter(link => !link.active);
	const activeChecked = activeLinks.filter(link => link.selected).length;
	const inactiveChecked = inactiveLinks.filter(link => link.selected).length;
	const overlappingLinks = GetOverlappingTimePeriods(links);

	return (<>
		<ModalHeader Title={bucketPluralLabel} Close={props.Close} />
		<div className="modal-body mt-0">
			<Grid container>
				<Autocomplete
					multiple
					className="w-100 sticky-top pt-5"
					id={`budget-bucket-links-${bucketTypeKey}`}
					options={availableNodes.map(node => node.bucketNodeId)}
					getOptionLabel={GetValueFromId}
					value={[]}
					filterSelectedOptions
					renderInput={(params) => (
						<TextField
							{...params}
							label={`Add ${bucketPluralLabel}`}
							className="bg-white"
						/>
					)}
					onChange={OnChange}
				/>
				{availableNodes.length === 0 && (
					<Grid item xs={12} className="text-app mt-3">
						No {bucketPluralLabel.toLowerCase()} are available yet.
					</Grid>
				)}
				<Grid item xs={12}>
					<h3 className="text-app text-center mt-5">Active</h3>
					<Divider />
				</Grid>
				<Grid item xs={10} xl={6} className="text-start">
					<DropdownMenu
						OpenElement={
							<IconButton className="text-app" aria-label='edit menu'>
								{activeChecked === 0 ?
									<Square className='mx-1 px-1' />
									: activeChecked === activeLinks.length ?
										<CheckSquareFill className='mx-1 px-1' />
										:
										<DashSquare className='mx-1 px-1' />
								}
								<h6 className="mb-0">Select Multiple ({activeChecked} Selected)</h6>
							</IconButton>
						}
						Items={AddSelectionMenuItems(true)}>
					</DropdownMenu>
				</Grid>
				{activeChecked > 0 &&
					<Grid item xs={2} xl={6} className="text-end">
						<DropdownMenu
							OpenElement={
								<IconButton className="text-app" aria-label='edit menu'>
									{!isMobile ? <>
										<h6 className="mb-0">Action&nbsp;</h6>
										<ThreeDots />
									</> :
										<ThreeDotsVertical />
									}
								</IconButton>
							}
							Items={AddActionItems(true)}>
						</DropdownMenu>
					</Grid>
				}
				{activeLinks.map(link => {
					const rowIndex = links.indexOf(link);
					return (
						<Grid item xs={12} key={`${link.item.bucketNodeId}-${rowIndex}-active`}>
							<BudgetBucketNodeLimited
								Active={link}
								BucketLabel={bucketLabel}
								Checked={link.selected}
								DisplayName={GetDisplayName(link.item.bucketNodeId)}
								Index={rowIndex}
								IsOverlap={overlappingLinks.includes(link)}
								OnChecked={UpdateChecked}
							/>
						</Grid>
					);
				})}
				{inactiveLinks.length > 0 &&
					<Accordion className="w-100 my-5">
						<AccordionSummary expandIcon={<ChevronDown />}>
							<Typography>Inactive {bucketPluralLabel}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid container>
								<Grid item xs={6} className="text-start">
									<DropdownMenu
										OpenElement={
											<IconButton className="text-app" aria-label='edit menu'>
												{inactiveChecked === 0 ?
													<Square className='mx-1 px-1' />
													: inactiveChecked === inactiveLinks.length ?
														<CheckSquareFill className='mx-1 px-1' />
														:
														<DashSquare className='mx-1 px-1' />
												}
												<h6 className="mb-0">Select Multiple ({inactiveChecked} Selected)</h6>
											</IconButton>
										}
										Items={AddSelectionMenuItems(false)}>
									</DropdownMenu>
								</Grid>
								{inactiveChecked > 0 &&
									<Grid item xs={6} className="text-end">
										<DropdownMenu
											OpenElement={
												<IconButton className="text-app" aria-label='edit menu'>
													{!isMobile ? <>
														<h6 className="mb-0">Action&nbsp;</h6>
														<ThreeDots />
													</> :
														<ThreeDotsVertical />
													}
												</IconButton>
											}
											Items={AddActionItems(false)}>
										</DropdownMenu>
									</Grid>
								}
								{inactiveLinks.map(link => {
									const rowIndex = links.indexOf(link);
									return (
										<Grid item xs={12} key={`${link.item.bucketNodeId}-${rowIndex}-inactive`}>
											<BudgetBucketNodeLimited
												Active={link}
												BucketLabel={bucketLabel}
												Checked={link.selected}
												DisplayName={GetDisplayName(link.item.bucketNodeId)}
												Index={rowIndex}
												IsOverlap={overlappingLinks.includes(link)}
												OnChecked={UpdateChecked}
											/>
										</Grid>
									);
								})}
							</Grid>
						</AccordionDetails>
					</Accordion>
				}
			</Grid>
			{showStart &&
				<EditTimePeriodDate Label="Activation Date" Close={() => setShowStart(false)} Confirm={UpdateStartDateMultiple} />
			}
			{showEnd &&
				<EditTimePeriodDate Label="Expiration Date" Close={() => setShowEnd(false)} Confirm={UpdateEndDateMultiple} />
			}
		</div>
		<ModalFooterSaveCancel ShowSave={ShouldShowSave()} Save={Save} Close={props.Close} />
	</>);

	function GetValueFromId(id: any) {
		return GetDisplayName(id);
	}

	function GetDisplayName(bucketNodeId: number) {
		return nodeById[bucketNodeId]?.displayName ?? links.find(link => link.item.bucketNodeId === bucketNodeId)?.item.displayName ?? bucketNodeId.toString();
	}

	function OnChange(event: any, newValue: any[]) {
		if (!newValue || newValue.length === 0) return;
		AddBucketNode(newValue[0]);
	}

	function AddBucketNode(bucketNodeId: number) {
		const node = nodeById[bucketNodeId];
		if (!node) return;

		const nextItem = {
			budgetBucketNodeId: 0,
			bucketNodeId: node.bucketNodeId,
			bucketTypeKey: node.bucketTypeKey,
			displayName: node.displayName,
			isInactive: node.isInactive,
			isViewTransactions: node.isViewTransactions,
			startDate: null,
			endDate: null,
		} as BudgetBucketNodeItem;

		setLinks(BuildExtendedLinks([...links.map(link => link.item), nextItem], bucketTypeKey, nodeById, links));
	}

	function UpdateChecked(index: number, newValue: boolean) {
		const nextLinks = [...links];
		nextLinks[index] = { ...nextLinks[index], selected: newValue };
		setLinks(nextLinks);
	}

	function UpdateCheckedMultiple(option: string, isActive: boolean) {
		const nextLinks = [...links];
		switch (option) {
			case "all":
				nextLinks.forEach((link, index) => {
					if (link.active === isActive) nextLinks[index] = { ...link, selected: true };
				});
				break;
			case "new":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && !!link.isNew };
				});
				break;
			case "updated":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && !!link.isUpdated };
				});
				break;
			case "unlimited":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && link.item.startDate === null && link.item.endDate === null };
				});
				break;
			case "nostart":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && link.item.startDate === null };
				});
				break;
			case "noend":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && link.item.endDate === null };
				});
				break;
			case "start":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && link.item.startDate !== null };
				});
				break;
			case "end":
				nextLinks.forEach((link, index) => {
					nextLinks[index] = { ...link, selected: link.active === isActive && link.item.endDate !== null };
				});
				break;
			case "none":
				nextLinks.forEach((link, index) => {
					if (link.active === isActive) nextLinks[index] = { ...link, selected: false };
				});
				break;
		}
		setLinks(nextLinks);
	}

	function UpdateStartDateMultiple(newValue: DateTime | null) {
		const nextLinks = links.map(link => {
			if (!link.selected) return link;
			const item = { ...link.item, startDate: newValue };
			return {
				...link,
				item,
				selected: false,
				isUpdated: true,
				active: IsCurrentlyActive(item),
			};
		});
		setLinks(nextLinks);
		setShowStart(false);
	}

	function UpdateEndDateMultiple(newValue: DateTime | null) {
		const nextLinks = links.map(link => {
			if (!link.selected) return link;
			const item = { ...link.item, endDate: newValue };
			return {
				...link,
				item,
				selected: false,
				isUpdated: true,
				active: IsCurrentlyActive(item),
			};
		});
		setLinks(nextLinks);
		setShowEnd(false);
	}

	function RemoveMultiple(isActive: boolean) {
		setLinks(links.filter(link => !(link.selected && link.active === isActive)));
	}

	function AddSelectionMenuItems(isActive: boolean) {
		const checkedCount = isActive ? activeChecked : inactiveChecked;
		const scopedLinks = isActive ? activeLinks : inactiveLinks;
		const result: any[] = [];

		if (checkedCount < scopedLinks.length) {
			result.push({ Text: "Select All", OnClick: () => UpdateCheckedMultiple("all", isActive) });
		}
		result.push({ Text: "All New", OnClick: () => UpdateCheckedMultiple("new", isActive) });
		result.push({ Text: "All Updated", OnClick: () => UpdateCheckedMultiple("updated", isActive) });
		if (isActive) {
			result.push({ Text: "No Time Limit", OnClick: () => UpdateCheckedMultiple("unlimited", true) });
		}
		result.push({ Text: "No Activation Date", OnClick: () => UpdateCheckedMultiple("nostart", isActive) });
		result.push({ Text: "No Expiration Date", OnClick: () => UpdateCheckedMultiple("noend", isActive) });
		result.push({ Text: isActive ? "Has Activation Date" : "Future Activation", OnClick: () => UpdateCheckedMultiple("start", isActive) });
		result.push({ Text: isActive ? "Has Expiration Date" : "Already Expired", OnClick: () => UpdateCheckedMultiple("end", isActive) });
		if (checkedCount > 0) {
			result.push({ Text: "Deselect All", OnClick: () => UpdateCheckedMultiple("none", isActive) });
		}
		return result;
	}

	function AddActionItems(isActive: boolean) {
		return [
			{ Text: "Remove", OnClick: () => RemoveMultiple(isActive) },
			{ Text: "Set Activation Date", OnClick: () => setShowStart(true) },
			{ Text: "Set Expiration Date", OnClick: () => setShowEnd(true) },
		];
	}

	function ShouldShowSave() {
		return overlappingLinks.length === 0;
	}

	function Save() {
		const nextBudget = {
			...budget,
			bucketNodes: [
				...(budget.bucketNodes ?? []).filter(link => link.bucketTypeKey.toLowerCase() !== bucketTypeKey.toLowerCase()),
				...links.map(link => link.item),
			],
		} as BudgetItem;
		props.OnSave(nextBudget);
	}
};

interface BudgetBucketNodeLimitedProps {
	Active: BudgetBucketNodeExtended,
	BucketLabel: string,
	Checked: boolean,
	DisplayName: string,
	Index: number,
	IsOverlap: boolean,
	OnChecked: (index: number, value: boolean) => void,
}

const BudgetBucketNodeLimited = (props: BudgetBucketNodeLimitedProps) => {
	const active = props.Active;
	const [expanded, setExpanded] = useState(props.IsOverlap);
	const [checked, setChecked] = useState(props.Checked);

	useEffect(() => {
		setChecked(props.Checked);
	}, [props.Checked]);

	useEffect(() => {
		if (props.IsOverlap) setExpanded(true);
	}, [props.IsOverlap]);

	return (
		<Grid container className="py-2" style={{ textAlign: 'left' }}>
			<Accordion className="w-100" expanded={expanded}>
				<AccordionSummary expandIcon={<ChevronDown />} onClick={() => setExpanded(!expanded)}>
					<Grid container>
						{!props.IsOverlap &&
							<Grid item xs={2} xl={1}>
								<Checkbox checked={checked ?? false} onClick={(e: any) => e.stopPropagation()} onChange={SetChecked} />
							</Grid>
						}
						<Grid item alignContent="center" xs={10} xl={6}>
							<h3 className={`${props.IsOverlap ? 'text-danger' : 'text-app'} mb-0`}>{props.DisplayName}{active.isNew ? " (new)" : active.isUpdated ? " (updated)" : ""}</h3>
						</Grid>
						{!expanded &&
							<Grid item alignContent="center" xs={12} xl={5}>
								{GetActiveSummaryShort()}
							</Grid>
						}
					</Grid>
				</AccordionSummary>
				<AccordionDetails>
					<Grid item className={props.IsOverlap ? "text-danger" : undefined} alignContent="center" xs={12}>
						{props.IsOverlap ? GetOverlapSummary() : GetActiveSummary()}
					</Grid>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);

	function SetChecked(event: any, newValue: boolean) {
		setChecked(newValue);
		props.OnChecked(props.Index, newValue);
	}

	function GetActiveSummary() {
		if (active.item.startDate && active.item.endDate) {
			return `Active between ${active.item.startDate.toString()} and ${active.item.endDate.toString()}`;
		} else if (active.item.startDate) {
			return `Active ${active.active ? 'since' : 'starting'} ${active.item.startDate.toString()}`;
		} else if (active.item.endDate) {
			return `${active.active ? 'Expires' : 'Expired'} ${active.item.endDate.toString()}`;
		}
		return `${props.BucketLabel} is always active`;
	}

	function GetOverlapSummary() {
		if (active.item.startDate && active.item.endDate) {
			return `Overlapping Dates: Active between ${active.item.startDate.toString()} and ${active.item.endDate.toString()}`;
		} else if (active.item.startDate) {
			return `Overlapping Dates: Active ${active.active ? 'since' : 'starting'} ${active.item.startDate.toString()}`;
		} else if (active.item.endDate) {
			return `Overlapping Dates: ${active.active ? 'Expires' : 'Expired'} ${active.item.endDate.toString()}`;
		}
		return `Overlapping Dates: ${props.BucketLabel} is always active`;
	}

	function GetActiveSummaryShort() {
		if (active.item.startDate && active.item.endDate) {
			return `${active.item.startDate.toString()} - ${active.item.endDate.toString()}`;
		} else if (active.item.startDate) {
			return `${active.active ? 'Since' : 'Starting'} ${active.item.startDate.toString()}`;
		} else if (active.item.endDate) {
			return `${active.active ? 'Expires' : 'Expired'} ${active.item.endDate.toString()}`;
		}
		return '';
	}
};

function BuildExtendedLinks(items: BudgetBucketNodeItem[], bucketTypeKey: string, nodeById: Record<number, BucketNodeItem>, previousLinks?: BudgetBucketNodeExtended[]) {
	const previousByKey = new Map((previousLinks ?? []).map((link, index) => [GetLinkKey(link.item, index), link]));
	return SortLinks(items.filter(item => item.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase()), nodeById)
		.map((item, index) => {
			const previous = previousByKey.get(GetLinkKey(item, index));
			return {
				item: {
					...item,
					displayName: nodeById[item.bucketNodeId]?.displayName ?? item.displayName,
				},
				selected: false,
				active: IsCurrentlyActive(item),
				isNew: previous?.isNew ?? item.budgetBucketNodeId === 0,
				isUpdated: previous?.isUpdated ?? false,
			} as BudgetBucketNodeExtended;
		});
}

function SortLinks(items: BudgetBucketNodeItem[], nodeById: Record<number, BucketNodeItem>) {
	return [...items].sort((a, b) => {
		const nameCompare = (nodeById[a.bucketNodeId]?.displayName ?? a.displayName ?? "").localeCompare(nodeById[b.bucketNodeId]?.displayName ?? b.displayName ?? "");
		if (nameCompare !== 0) return nameCompare;

		const firstStart = new Date(b.startDate?.Value ?? 0);
		firstStart.setHours(0, 0, 0, 0);
		const secondStart = new Date(a.startDate?.Value ?? 0);
		secondStart.setHours(0, 0, 0, 0);
		if (secondStart.getTime() !== firstStart.getTime()) return secondStart.getTime() - firstStart.getTime();

		const firstEnd = new Date(b.endDate?.Value ?? '2999-12-31');
		firstEnd.setHours(0, 0, 0, 0);
		const secondEnd = new Date(a.endDate?.Value ?? '2999-12-31');
		secondEnd.setHours(0, 0, 0, 0);
		return secondEnd.getTime() - firstEnd.getTime();
	});
}

function GetLinkKey(item: BudgetBucketNodeItem, index: number) {
	return `${item.bucketNodeId}|${item.startDate?.toJSON?.() ?? ''}|${item.endDate?.toJSON?.() ?? ''}|${item.budgetBucketNodeId ?? index}`;
}

function IsCurrentlyActive(item: BudgetBucketNodeItem) {
	const current = new DateTime();
	if (item.startDate && item.startDate > current) return false;
	if (item.endDate && item.endDate < current) return false;
	return true;
}

function GetOverlappingTimePeriods(links: BudgetBucketNodeExtended[]) {
	const overlap = [] as BudgetBucketNodeExtended[];

	for (const link of links) {
		const matches = links.filter(candidate => candidate.item.bucketNodeId === link.item.bucketNodeId && candidate !== link);
		if (matches.length === 0) continue;

		const isOverlap =
			(matches.filter(match => match.item.startDate === link.item.startDate).length > 0) ||
			(matches.filter(match => match.item.endDate === link.item.endDate).length > 0) ||
			(matches.filter(match => match.item.startDate && link.item.endDate && match.item.startDate === link.item.endDate).length > 0) ||
			(matches.filter(match => match.item.endDate && link.item.startDate && match.item.endDate === link.item.startDate).length > 0) ||
			(matches.filter(match => !match.item.endDate && !match.item.startDate).length > 0) ||
			(matches.filter(_match => !link.item.endDate && !link.item.startDate).length > 0) ||
			(matches.filter(match => !match.item.startDate && link.item.startDate && link.item.startDate < match.item.endDate!).length > 0) ||
			(matches.filter(match => !match.item.endDate && link.item.endDate && link.item.endDate > match.item.startDate!).length > 0) ||
			(matches.filter(match => !link.item.startDate && match.item.startDate && match.item.startDate < link.item.endDate!).length > 0) ||
			(matches.filter(match => !link.item.endDate && match.item.endDate && match.item.endDate > link.item.startDate!).length > 0) ||
			(matches.filter(match => match.item.startDate && link.item.startDate && match.item.startDate < link.item.startDate && match.item.endDate! > link.item.startDate).length > 0) ||
			(matches.filter(match => match.item.startDate && link.item.endDate && match.item.startDate < link.item.endDate && match.item.endDate! > link.item.endDate).length > 0);

		if (isOverlap) overlap.push(link);
	}

	return overlap;
}

export default EditBudgetBucketNodes;